'use client';

import type { Route } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { type CourseSummary, formatMinutes } from '@/lib/courses';
import { useTranslation } from '@/lib/i18n';

/** Build the typed detail route for a course slug. */
function courseHref(slug: string): Route {
  // WHY: typedRoutes types this template as Route at the call site; the
  // cast documents that the slug is an app-owned, URL-safe segment.
  return `/courses/${slug}` as Route;
}

/**
 * Course thumbnail with a play-glyph placeholder fallback.
 *
 * @remarks Uses next/image (no-img-element). External hosts are permitted
 *   via next.config images.remotePatterns.
 */
function Thumbnail(props: {
  readonly url: string | null;
  readonly alt: string;
  readonly glyphClassName: string;
}): React.JSX.Element {
  if (props.url) {
    return (
      <Image src={props.url} alt={props.alt} fill sizes='100vw' className='object-cover' />
    );
  }
  return (
    <div className='flex size-full items-center justify-center bg-gradient-to-br from-purple-100 to-purple-200'>
      <span className={props.glyphClassName} aria-hidden='true'>
        ▶
      </span>
    </div>
  );
}

/** Inline price block shared by both card layouts. */
function PriceTag(props: { readonly course: CourseSummary }): React.JSX.Element {
  const { t } = useTranslation();
  const { course } = props;
  if (course.is_free) {
    return <span className='font-bold text-green-600'>{t('common.free')}</span>;
  }
  const discount =
    course.compare_at_price !== null && course.compare_at_price > course.price
      ? course.compare_at_price
      : null;
  return (
    <div className='flex items-center gap-2'>
      <span className='font-bold'>{t('courses.price', { price: course.price })}</span>
      {discount === null ? null : (
        <span className='text-sm line-through opacity-70'>
          {t('courses.price', { price: discount })}
        </span>
      )}
    </div>
  );
}

/** Grid (poster) card for a course summary. */
export function CourseCard(props: {
  readonly course: CourseSummary;
  readonly featured?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { course, featured = false } = props;
  return (
    <Link
      href={courseHref(course.slug)}
      className={`group overflow-hidden rounded-xl border bg-white transition-shadow hover:shadow-lg ${
        featured ? 'border-purple-200' : ''
      }`}
    >
      <div className='relative aspect-video bg-gray-100'>
        <Thumbnail
          url={course.thumbnail_url}
          alt={course.title}
          glyphClassName='text-4xl text-purple-400'
        />
        {featured ? (
          <span className='absolute right-3 top-3 rounded bg-yellow-500 px-2 py-1 text-xs font-semibold text-white'>
            {t('courses.featuredBadge')}
          </span>
        ) : null}
      </div>
      <div className='p-4'>
        <h3 className='line-clamp-2 font-semibold transition-colors group-hover:text-purple-600'>
          {course.title}
        </h3>
        <p className='mt-1 text-sm opacity-70'>{course.instructor_name}</p>
        <div className='mt-3 flex items-center gap-4 text-sm opacity-70'>
          <span>{formatMinutes(course.total_duration_minutes)}</span>
          <span>{t('common.lessonsCount', { count: course.lesson_count })}</span>
        </div>
        {course.review_count > 0 && (
          <div className='mt-2 flex items-center gap-2'>
            <span className='font-semibold'>{course.average_rating.toFixed(1)}</span>
            <span className='text-sm opacity-70'>
              {t('common.reviewsParen', { count: course.review_count })}
            </span>
          </div>
        )}
        <div className='mt-3 border-t pt-3'>
          <PriceTag course={course} />
        </div>
      </div>
    </Link>
  );
}

/** Horizontal (list) card for a course summary. */
export function CourseCardList(props: { readonly course: CourseSummary }): React.JSX.Element {
  const { t } = useTranslation();
  const { course } = props;
  return (
    <Link
      href={courseHref(course.slug)}
      className='group flex gap-6 overflow-hidden rounded-xl border bg-white p-4 transition-shadow hover:shadow-lg'
    >
      <div className='relative h-36 w-64 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100'>
        <Thumbnail
          url={course.thumbnail_url}
          alt={course.title}
          glyphClassName='text-3xl text-purple-400'
        />
      </div>
      <div className='min-w-0 flex-1'>
        <div className='flex items-start justify-between gap-4'>
          <div>
            <h3 className='text-lg font-semibold transition-colors group-hover:text-purple-600'>
              {course.title}
            </h3>
            <p className='mt-1 text-sm opacity-70'>{course.instructor_name}</p>
          </div>
          <div className='flex-shrink-0 text-right'>
            <PriceTag course={course} />
          </div>
        </div>
        <p className='mt-2 line-clamp-2 text-sm opacity-70'>{course.short_description}</p>
        <div className='mt-3 flex items-center gap-6 text-sm opacity-70'>
          <span>{formatMinutes(course.total_duration_minutes)}</span>
          <span>{t('common.lessonsCount', { count: course.lesson_count })}</span>
          <span>{t('courses.studentsCount', { count: course.enrollment_count.toLocaleString() })}</span>
          {course.review_count > 0 && (
            <span>
              {t('courses.ratingReviews', {
                rating: course.average_rating.toFixed(1),
                count: course.review_count,
              })}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
